import { memo, useCallback, useMemo } from "react";

import { useCustomDnD } from "@/hooks/use-dnd/use-custom-dnd";

import type { FormOptionType } from "@/components/ui/form/_types/type";
import { CheckboxGroupField } from "@/components/ui/form/fields/check-box-field/check-box-group";
import DragIndicatorIcon from "@/components/ui/icon/drag-indicator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table/table";

import { FormFieldLayout } from "../../../layouts";
import { SettingHelpText } from "../setting-help-text";
import { Empty } from "./empty";

import type { CheckedState } from "@radix-ui/react-checkbox";

type Discount = {
	id: string;
	name: string;
	discountID: string;
};

type Props = {
	// 通常・連泊ディスカウント
	ordinaryAndLongTermDiscounts: Array<Discount>;
	// 適用ディスカウント
	multiDiscounts: Array<string>;

	// 適用ディスカウントの追加
	onAddMultiDiscounts: (value: string) => void;
	// 適用ディスカウントの変更
	onChangeMultiDiscounts: (values: Array<string>) => void;
	// 適用ディスカウントの削除
	onDeleteMultiDiscounts: (value: string) => void;
};

export const MultiFormFieldComposition = memo(
	({
		ordinaryAndLongTermDiscounts,
		multiDiscounts,
		onAddMultiDiscounts,
		onChangeMultiDiscounts,
		onDeleteMultiDiscounts,
	}: Props) => {
		/**
		 * 適用するディスカウント
		 */
		const applyDiscounts = useMemo(() => {
			/**
			 * multiDiscounts の配列順 = 優先度なので、multiDiscounts を key として生成する
			 */
			return multiDiscounts
				.filter((discountID) =>
					ordinaryAndLongTermDiscounts.some((discount) => discountID === discount.discountID),
				)
				.map((discountID) => {
					const tar = ordinaryAndLongTermDiscounts.find((discount) => discount.discountID === discountID);
					return tar!;
				});
		}, [ordinaryAndLongTermDiscounts, multiDiscounts]);

		const { dndItemList: applyDiscountsDnD, onAddDnDItem, onDeleteDnDItem } = useCustomDnD(applyDiscounts);
		/**
		 * 適用するディスカウントのオプション
		 */
		const applyDiscountsValues = useMemo((): Array<string> => {
			return applyDiscountsDnD.map(({ value: { discountID } }) => discountID);
		}, [applyDiscountsDnD]);
		/**
		 * 適用するディスカウントの DnD を追加
		 */
		const onAddApplyDiscountDnD = useCallback(
			(discountID: string) => {
				/**
				 * 適用するディスカウントの抽出
				 */
				const tar = ordinaryAndLongTermDiscounts.find((discount) => discount.discountID === discountID);

				/**
				 * 適用するディスカウントの DnD を追加
				 */
				tar && onAddDnDItem(tar);
			},
			[onAddDnDItem, ordinaryAndLongTermDiscounts],
		);
		/**
		 * 適用するディスカウントの DnD を削除
		 */
		const onDeleteApplyDiscountDnD = useCallback(
			(discountID: string) => {
				/**
				 * 削除するディスカウントの抽出
				 */
				const tar = ordinaryAndLongTermDiscounts.find((discount) => discount.discountID === discountID);
				tar && onDeleteDnDItem(tar);
			},
			[onDeleteDnDItem, ordinaryAndLongTermDiscounts],
		);

		/**
		 * 通常・連泊ディスカウントのオプション
		 */
		const ordinaryAndLongTermDiscountsOptions = useMemo((): Array<FormOptionType> => {
			return ordinaryAndLongTermDiscounts.map((discount) => ({
				id: discount.discountID,
				label: discount.name,
				value: discount.discountID,
			}));
		}, [ordinaryAndLongTermDiscounts]);

		/**
		 * 通常・連泊ディスカウントの変更
		 */
		const handleChangeOrdinaryAndLongTermDiscountsOptions = useCallback(
			(checked: CheckedState, item: FormOptionType) => {
				if (checked) {
					// ViewModel の更新
					onAddMultiDiscounts(item.value);
					// DnD の更新
					onAddApplyDiscountDnD(item.value);
				} else {
					// ViewModel の更新
					onDeleteMultiDiscounts(item.value);
					// DnD の更新
					onDeleteApplyDiscountDnD(item.value);
				}
			},
			[onAddApplyDiscountDnD, onAddMultiDiscounts, onDeleteApplyDiscountDnD, onDeleteMultiDiscounts],
		);

		/**
		 * 適用するディスカウントの順序を変更
		 */
		const handleChangeMultiDiscounts = useCallback(() => {
			/**
			 * MultiDiscounts の型へ変換
			 */
			const convertedMultiDiscounts = applyDiscountsDnD.map((discount) => discount.value.discountID);

			// ViewModel の更新
			onChangeMultiDiscounts(convertedMultiDiscounts);
		}, [applyDiscountsDnD, onChangeMultiDiscounts]);

		/**
		 * ViewModel でエラーを保持しないようにしている
		 * 適用ディスカウントの場合、配列の要素全体に対してバリデーションを行うため、現行の ViewModel ではカバーすることができない
		 */
		const hasError = useMemo(() => applyDiscounts.length === 0, [applyDiscounts.length]);

		return (
			<div className="grid-rows xs:flex xs:gap-x-2 grid gap-y-5 p-3">
				<div className="sm:flex-1">
					<FormFieldLayout title="通常・連泊ディスカウント">
						{ordinaryAndLongTermDiscountsOptions.length > 0 ? (
							<>
								<CheckboxGroupField
									value={applyDiscountsValues}
									options={ordinaryAndLongTermDiscountsOptions}
									handleCheckedChange={handleChangeOrdinaryAndLongTermDiscountsOptions}
									errorMessage={hasError ? "適用ディスカウントは選択が必須の項目です" : undefined}
								/>
								<div className="mt-2">
									<p className="whitespace-pre-wrap text-sm text-caption-50">
										適用したいディスカウント選択すると、下部の
										<b>適用するディスカウント</b>
										に表示されます。
									</p>
								</div>
							</>
						) : (
							<Empty text="通常・連泊ディスカウントが作成、もしくは公開されていません。" />
						)}
					</FormFieldLayout>
				</div>

				<div className="sm:flex-[2]">
					<FormFieldLayout title="適用するディスカウント">
						{applyDiscountsDnD.length > 0 ? (
							<>
								<Table>
									<TableHeader>
										<TableRow className="hover:bg-transparent">
											<TableHead className="w-10" />

											<TableHead className="w-20" justifyStyle="center">
												優先度
											</TableHead>

											<TableHead justifyStyle="center">ディスカウント名</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{applyDiscountsDnD.map((discount, index) => {
											const displayIndex = `${index + 1}. `;

											return (
												<TableRow
													key={discount.key}
													ref={discount.events.ref}
													onMouseDown={discount.events.onMouseDown}
													onMouseUp={() => handleChangeMultiDiscounts()}
												>
													<TableCell>
														{/* DragIcon */}
														<div className="cursor-grabbing">
															<DragIndicatorIcon />
														</div>
													</TableCell>

													{/* 優先度 */}
													<TableCell className="text-center">
														<p>{displayIndex}</p>
													</TableCell>

													{/* ディスカウント名 */}
													<TableCell>
														<p>{discount.value.name}</p>
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>

								<div className="mt-2">
									<p className="whitespace-pre-wrap text-sm text-caption-50">
										設定した割引の優先度が高い順に適用されます。優先度はドラッグアンドドロップで変更可能です。
									</p>

									<p className="whitespace-pre-wrap text-sm text-caption-50">
										<SettingHelpText />
									</p>

									<p className="whitespace-pre-wrap text-sm text-caption-50">
										適用するディスカウントから削除する場合は、上部の<b>通常・連泊ディスカウント</b>
										から対象のディスカウントを選択解除してください。
									</p>
								</div>
							</>
						) : (
							<Empty text="適用するディスカウントが選択されていません。" />
						)}
					</FormFieldLayout>
				</div>
			</div>
		);
	},
);
